import React, { useEffect, useState } from "react";
import BaseService from "../../../service/base.service";
import { Row, Col, Card, Typography, Space, Alert } from "antd";
import {
  TeamOutlined,
  UserAddOutlined,
  CalendarOutlined,
  CheckCircleOutlined,
} from "@ant-design/icons";
import { Line } from "@ant-design/charts";
import PatientStatistics from "../../../models/patientStatistics";
import UserDashboardChartData from "../../../models/userDashboardChartData";
import LatestPatient from "../../../models/latestPatient";
import { useIsMobile } from "../../../hooks/useIsMobile";

const { Title, Text } = Typography;

const UserDashboardPage: React.FC = () => {
  const isMobile = useIsMobile();
  const [patientStatistics, setPatientStatistics] = useState<PatientStatistics>();
  const [loading, setLoading] = useState(true);
  const [chartData, setChartData] = useState<{ month: string; patients: number }[]>([]);
  const [, setLatestPatients] = useState<LatestPatient[]>([]);

  useEffect(() => {
    fetchPatientStatistics();
    fetchChartData();
    fetchLatestPatients();
  }, []);

  const fetchPatientStatistics = async () => {
    try {
      const rp = await BaseService.get<PatientStatistics>("/dashboard/statistics/patients", null);
      if (rp.Status) {
        const data = rp.Data;
        setPatientStatistics(
          new PatientStatistics(
            data.totalPatients,
            data.currentMonthPatients,
            data.upcomingAppointments
          )
        );
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchChartData = async () => {
    try {
      const rp = await BaseService.get<UserDashboardChartData[]>(
        "/dashboard/statistics/patients-chart",
        null
      );
      if (rp.Status) {
        const formattedData = rp.Data.map((d: UserDashboardChartData) => {
          const date = new Date(d.date);
          const monthYear = `${date.toLocaleString("default", {
            month: "short",
          })} ${date.getFullYear()}`;
          return { month: monthYear, patients: d.totalPatients };
        });
        setChartData(formattedData);
      }
    } catch (err) {
      console.error(err);
    }
  };

  const fetchLatestPatients = async () => {
    try {
      const rp = await BaseService.get<LatestPatient[]>(
        "/dashboard/statistics/latest-patients",
        null
      );
      if (rp.Status) {
        const patientsWithKey = rp.Data.map((p: LatestPatient, idx: number) => ({
          ...p,
          key: idx + 1,
        }));
        setLatestPatients(patientsWithKey);
      }
    } catch (err) {
      console.error(err);
    }
  };

  const cards = [
    {
      title: "Total Patients",
      value: patientStatistics?.totalPatients ?? "—",
      icon: <TeamOutlined style={{ fontSize: isMobile ? 24 : 32, color: "#1890ff" }} />,
      color: "#e6f7ff",
    },
    {
      title: "New This Month",
      value: patientStatistics?.currentMonthPatients ?? "—",
      icon: <UserAddOutlined style={{ fontSize: isMobile ? 24 : 32, color: "#52c41a" }} />,
      color: "#f6ffed",
    },
    {
      title: "Upcoming Appointments",
      value: patientStatistics?.upcomingAppointments ?? "—",
      icon: <CalendarOutlined style={{ fontSize: isMobile ? 24 : 32, color: "#faad14" }} />,
      color: "#fffbe6",
    },
    {
      title: "Recovered Patients",
      value: "-",
      icon: <CheckCircleOutlined style={{ fontSize: isMobile ? 24 : 32, color: "#722ed1" }} />,
      color: "#f9f0ff",
    },
  ];

  const chartHeight = isMobile ? 220 : 320;

  const chartConfig = {
    data: chartData,
    xField: "month",
    yField: "patients",
    smooth: true,
    point: { size: isMobile ? 3 : 5, shape: "circle" },
    color: "#1890ff",
    xAxis: {
      label: {
        rotate: isMobile ? -60 : -45,
        style: { textAlign: "right", fontSize: isMobile ? 10 : 12 },
        autoHide: isMobile,
        autoRotate: false,
      },
    },
    height: chartHeight,
    loading,
    autoFit: true,
  };

  return (
    <div
      style={{
        flex: 1,
        display: "flex",
        flexDirection: "column",
        minHeight: 0,
        height: "100%",
        padding: isMobile ? "8px 0 16px" : "16px 16px 24px",
        overflowY: "auto",
        overflowX: "hidden",
        boxSizing: "border-box",
        WebkitOverflowScrolling: "touch",
      }}
    >
      <div
        style={{
          width: "100%",
          maxWidth: 1400,
          display: "flex",
          flexDirection: "column",
          flex: 1,
          gap: isMobile ? 16 : 24,
          margin: "0 auto",
        }}
      >
        <Alert
          message={
            isMobile
              ? "Overview of patient statistics and trends."
              : "Your personalized dashboard gives you an overview of patient statistics and trends."
          }
          type="info"
          showIcon={!isMobile}
          style={{
            background: "#f0f7ff",
            borderColor: "#d0e4ff",
            color: "#2c5282",
            borderRadius: 8,
            padding: isMobile ? "8px 12px" : "10px 14px",
            fontSize: isMobile ? 13 : 14,
          }}
        />

        <Row gutter={[isMobile ? 12 : 16, isMobile ? 12 : 16]}>
          {cards.map((card, idx) => (
            <Col xs={12} sm={12} md={6} key={idx}>
              <Card
                hoverable={!isMobile}
                styles={{ body: { padding: isMobile ? 12 : 24 } }}
                style={{
                  textAlign: "center",
                  backgroundColor: card.color,
                  borderRadius: isMobile ? 10 : 12,
                  height: "100%",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
                }}
              >
                <Space direction="vertical" size={isMobile ? 6 : 12} align="center">
                  {card.icon}
                  <Title
                    level={4}
                    style={{
                      margin: 0,
                      fontSize: isMobile ? 13 : undefined,
                      lineHeight: 1.3,
                    }}
                  >
                    {card.title}
                  </Title>
                  <Text strong style={{ fontSize: isMobile ? 18 : 20 }}>
                    {card.value}
                  </Text>
                </Space>
              </Card>
            </Col>
          ))}
        </Row>

        <Card
          title="Patients Added per Month"
          style={{
            borderRadius: isMobile ? 10 : 12,
            boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
            background: "#fff",
          }}
          styles={{
            header: { padding: isMobile ? "12px 16px" : undefined, fontSize: isMobile ? 14 : undefined },
            body: { padding: isMobile ? "8px 8px 12px" : "16px 24px", overflowX: "hidden" },
          }}
        >
          {chartData.length > 0 ? (
            <Line
              {...chartConfig}
              style={{ width: "100%", height: chartHeight }}
            />
          ) : (
            <div
              style={{
                height: chartHeight,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#999",
              }}
            >
              <Text type="secondary">No chart data available</Text>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
};

export default UserDashboardPage;
